/*
 * SundayBuilder · RCL Track Toggle (Track 1 · Track 2)
 * ---------------------------------------------------------------------
 * Minimal inline toggle rendered NEXT TO the existing readings line
 * in the DossierFolio Services section. Shown only when the resolved
 * proper is a Sundays-after-Pentecost proper with dual OT/Psalm
 * tracks (api response `has_dual_track: true`).
 *
 * Behaviour
 *   · Defaults to the parish's `preferred_rcl_track` (returned by the
 *     API as `parish_default_track`).
 *   · Plan-level override — never mutates the Parish Profile.
 *   · Emits the effective track upward via `onChange` so the parent
 *     can (a) update the displayed readings and (b) thread the track
 *     to LI suggestions and the dossier.
 * =================================================================== */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./rcl-track-toggle.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 *   properId   · authoritative lectionary proper id (e.g. "proper_15_year_b")
 *   value      · currently selected track ("track_1" | "track_2" | null)
 *   onChange   · (track, envelope) → void — envelope is the raw API response
 *                (has ot / psalm / epistle / gospel / has_dual_track / …)
 *   isRcl      · false when the parish tradition doesn't use RCL. Hides.
 */
export default function RCLTrackToggle({ properId, value, onChange, isRcl }) {
  const [env, setEnv] = useState(null);   // API envelope
  const [err, setErr] = useState(null);
  const active = useMemo(
    () => (value === "track_2" ? "track_2" : "track_1"),
    [value]
  );

  useEffect(() => {
    let cancelled = false;
    if (!properId || !isRcl) { setEnv(null); return; }
    setErr(null);
    axios.get(`${API}/lectionary/rcl-proper`, { params: { proper_id: properId, track: value || "track_1" } })
      .then((r) => {
        if (cancelled) return;
        setEnv(r.data);
        // If parent hasn't chosen yet, emit the parish default so
        // downstream surfaces (readings, LI) align immediately.
        if (!value && r.data.has_dual_track && r.data.parish_default_track) {
          onChange && onChange(r.data.parish_default_track, r.data);
        } else if (!value && !r.data.has_dual_track) {
          // Single-track proper — still emit the envelope so parent can
          // populate readings from it, but no track selection is stored.
          onChange && onChange(null, r.data);
        }
      })
      .catch((e) => { if (!cancelled) setErr(e); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properId, isRcl]);

  // Re-fetch when the user flips the track so `env.ot / psalm` reflect
  // the effective track for the readings display.
  useEffect(() => {
    let cancelled = false;
    if (!properId || !isRcl || !env?.has_dual_track) return;
    axios.get(`${API}/lectionary/rcl-proper`, { params: { proper_id: properId, track: active } })
      .then((r) => { if (!cancelled) setEnv(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!isRcl || !properId || err) return null;
  if (!env || !env.has_dual_track) return null;

  const pick = (t) => {
    if (t === active) return;
    onChange && onChange(t, { ...env, track: t });
  };

  return (
    <span
      className="pfb-rcl-toggle"
      role="radiogroup"
      aria-label="Revised Common Lectionary reading track"
      data-testid="rcl-track-toggle"
    >
      <span className="pfb-rcl-toggle-label">Track</span>
      <button
        type="button"
        role="radio"
        aria-checked={active === "track_1"}
        className={`pfb-rcl-toggle-btn ${active === "track_1" ? "on" : ""}`}
        onClick={() => pick("track_1")}
        data-testid="rcl-track-toggle-track-1"
        title="Track 1 · semi-continuous Old Testament"
      >
        1
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={active === "track_2"}
        className={`pfb-rcl-toggle-btn ${active === "track_2" ? "on" : ""}`}
        onClick={() => pick("track_2")}
        data-testid="rcl-track-toggle-track-2"
        title="Track 2 · Old Testament paired with the Gospel"
      >
        2
      </button>
      {env.parish_default_track && env.parish_default_track === active && (
        <span className="pfb-rcl-default" title="This matches your Parish Profile default">parish default</span>
      )}
    </span>
  );
}

/** Small helper the parent uses to render the effective readings line
 * from the envelope this component fetches, so we never duplicate the
 * readings display. */
export function readingsFromEnvelope(env) {
  if (!env) return { readings: [], psalm: "" };
  const readings = [
    ...(env.ot || []),
    ...(env.epistle || []),
    ...(env.gospel || []),
  ];
  const psalm = (env.psalm && env.psalm[0]) || "";
  return { readings, psalm };
}
